<template>
  <div class="home-annual-pipeline">
    <div class="home-annual-pipeline__toolbar">
      <button
        type="button"
        class="home-bu-bar__chip"
        :class="{ 'home-bu-bar__chip--active': showArchive }"
        @click="showArchive = !showArchive"
      >
        {{ showArchive ? t('hideArchive') : t('showArchive') }}
      </button>
    </div>
    <div
      v-if="pending && !years.length"
      class="home-annual-pipeline__loading"
    >
      {{ t('loading') }}
    </div>
    <div
      v-else-if="!years.length"
      class="home-annual-pipeline__empty"
    >
      {{ t('empty') }}
    </div>
    <div
      v-else
      class="home-annual-pipeline__years"
    >
      <section
        v-for="year in years"
        :key="year"
        class="home-annual-pipeline__col"
      >
        <div class="home-annual-pipeline__year-head">
          {{ year }}
          <span class="home-annual-pipeline__year-count">{{ buckets[year].length }}</span>
        </div>
        <div class="home-annual-pipeline__scroll">
          <div
            v-if="!buckets[year].length"
            class="home-annual-pipeline__col-empty"
          >
            {{ t('noDrugs') }}
          </div>
          <HomeKanbanCard
            v-for="row in buckets[year]"
            :key="row.id"
            :title="cardTitle(row)"
            :tag="t('tagNewMaa')"
            tone="plan"
            :rows="cardRows(row)"
            :selected="selectedId != null && Number(row.id) === Number(selectedId)"
            @select="emit('select', { id: Number(row.id), category: 'planned_maa', row })"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
/**
 * Годовой Pipeline: колонки по Planned Approval.
 * Факт Submission/Approval подменяет план; архив — годы до текущего.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from 'soykajs/composables/api'
import { productDisplayName } from '@/lib/productCard'
import { formatHomeDateDisplay } from '@/lib/dateInput'
import { buildHomeKanbanQuery, productMatchesBu } from '@/views/home/kanban/columns'
import HomeKanbanCard from '@/views/home/kanban/HomeKanbanCard.vue'

const props = defineProps({
  /** Сколько лет вперёд от текущего (1 = только текущий год). */
  rangeYears: { type: Number, default: 1 },
  bu: { type: String, default: 'all' },
  search: { type: String, default: '' },
  selectedId: { type: Number, default: null },
})

const emit = defineEmits(['select'])

const { t, locale } = useI18n()
const preferEnglish = computed(() => locale.value === 'en')
const showArchive = ref(false)

const lm = ref(
  buildHomeKanbanQuery({
    category: 'planned_maa',
    pipelineMode: 'new_maa',
    search: props.search,
    onPage: 500,
  })
)

const { model, pending } = useApi({
  url: 'products',
  data: { total_count: 0, data: [] },
  query: lm,
  watch: lm,
  immediate: true,
})

watch(
  () => props.search,
  search => {
    lm.value = buildHomeKanbanQuery({
      category: 'planned_maa',
      pipelineMode: 'new_maa',
      search,
      onPage: 500,
    })
  }
)

function extractYear(value) {
  if (value == null || value === '') return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getFullYear()
  }
  const s = String(value).trim()
  const m = s.match(/\b(20\d{2})\b/)
  return m ? parseInt(m[1], 10) : null
}

/** Planned Approval; при наличии факта одобрения (RC issue) — он. */
function approvalDisplay(row) {
  const smoh = row?.card_smoh || {}
  const actual =
    row.rc_issue_date_en ||
    row.rc_issue_date_ru ||
    null
  if (actual) {
    return { value: actual, actual: true }
  }
  const planned =
    smoh.planned_approval_date ||
    row.planned_approval_date ||
    row.new_maa_approval_date ||
    null
  return { value: planned, actual: false }
}

/** Submission: факт из SMoH, иначе план. */
function submissionDisplay(row) {
  const smoh = row?.card_smoh || {}
  const actual = smoh.submission_actual_date || smoh.submission_date || null
  if (actual && smoh.submission_actual_date) {
    return { value: actual, actual: true }
  }
  if (smoh.submission_date && !smoh.planned_submission_date) {
    return { value: smoh.submission_date, actual: true }
  }
  if (smoh.submission_actual_date) {
    return { value: smoh.submission_actual_date, actual: true }
  }
  const planned =
    row.new_maa_submission_date ||
    smoh.planned_submission_date ||
    null
  // если submission_date есть и отличается от planned — факт
  if (smoh.submission_date && smoh.planned_submission_date && smoh.submission_date !== smoh.planned_submission_date) {
    return { value: smoh.submission_date, actual: true }
  }
  if (smoh.submission_date && !planned) {
    return { value: smoh.submission_date, actual: true }
  }
  return { value: planned || smoh.submission_date || null, actual: Boolean(smoh.submission_actual_date) }
}

const SYSTEM_START_YEAR = 2024

const buckets = computed(() => {
  const currentYear = new Date().getFullYear()
  const maxYear = currentYear + Math.max(1, Number(props.rangeYears) || 1) - 1
  const minYear = showArchive.value ? SYSTEM_START_YEAR : currentYear
  const map = {}
  for (let y = minYear; y <= maxYear; y += 1) {
    map[y] = []
  }
  for (const row of model.value?.data || []) {
    if (!productMatchesBu(row, props.bu)) continue
    const approval = approvalDisplay(row)
    const y = extractYear(approval.value)
    if (y == null || y < minYear || y > maxYear) continue
    map[y].push(row)
  }
  return map
})

const years = computed(() => Object.keys(buckets.value).map(Number).sort((a, b) => a - b))

function cardTitle(row) {
  return productDisplayName(row, preferEnglish.value) || String(row.id)
}

function cardRows(row) {
  const submission = submissionDisplay(row)
  const approval = approvalDisplay(row)
  return [
    {
      label: submission.actual ? t('rows.submission') : t('rows.plannedSubmission'),
      value: formatHomeDateDisplay(submission.value, { empty: '—' }),
    },
    {
      label: approval.actual ? t('rows.approval') : t('rows.plannedApproval'),
      value: formatHomeDateDisplay(approval.value, { empty: '—' }),
    },
  ]
}
</script>

<i18n>
{
  "ru": {
    "loading": "Загрузка…",
    "empty": "Нет препаратов в выбранном диапазоне",
    "noDrugs": "Нет препаратов",
    "tagNewMaa": "Planned New MAA",
    "showArchive": "Previous years archive",
    "hideArchive": "Hide archive",
    "rows": {
      "plannedSubmission": "Planned Submission",
      "plannedApproval": "Planned approval",
      "submission": "Submission (actual)",
      "approval": "Approval (actual)"
    }
  },
  "en": {
    "loading": "Loading…",
    "empty": "No drugs in selected range",
    "noDrugs": "No drugs",
    "tagNewMaa": "Planned New MAA",
    "showArchive": "Previous years archive",
    "hideArchive": "Hide archive",
    "rows": {
      "plannedSubmission": "Planned Submission",
      "plannedApproval": "Planned approval",
      "submission": "Submission (actual)",
      "approval": "Approval (actual)"
    }
  }
}
</i18n>

<style scoped>
.home-annual-pipeline__toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}
.home-annual-pipeline__years {
  display: flex;
  flex-direction: row;
  gap: 12px;
  overflow-x: auto;
  min-height: 200px;
}
.home-annual-pipeline__col {
  flex: 0 0 260px;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.home-annual-pipeline__year-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 800;
  font-size: 14px;
  color: #001f63;
  padding: 4px 2px;
}
.home-annual-pipeline__year-count {
  font-size: 11px;
  font-weight: 700;
  color: #7a8e98;
}
.home-annual-pipeline__scroll {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
}
.home-annual-pipeline__loading,
.home-annual-pipeline__empty,
.home-annual-pipeline__col-empty {
  padding: 16px;
  text-align: center;
  color: #7a8e98;
  font-size: 12px;
}
</style>
